import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserId } from "../utils/auth";

interface DraftEntry {
  formId: string;
  data: Record<string, unknown>;
  savedAt: string;
  expiresAt: string;
}

interface PreservedState {
  route: string;
  scrollPosition: { x: number; y: number };
  timestamp: string;
  expiresAt: string;
}

interface RecentlyEndedFlag {
  timestamp: string;
  expiresAt: string;
}

export interface PostMaintenanceRestoreResult {
  showReturnBanner: boolean;
  hasDraft: boolean;
  draftCount: number;
  draftRoute: string | null;
  draftTimestamp: string | null;
  restoreDraft: () => void;
  dismissBanner: () => void;
  dismissDraft: () => void;
  reopenBanner: () => void;
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() < Date.now();
}

function getRecentlyEndedFlag(): RecentlyEndedFlag | null {
  try {
    const raw = localStorage.getItem("seweb:maintenance:recentlyEnded");
    if (!raw) return null;
    const flag: RecentlyEndedFlag = JSON.parse(raw);
    if (isExpired(flag.expiresAt)) {
      localStorage.removeItem("seweb:maintenance:recentlyEnded");
      return null;
    }
    return flag;
  } catch {
    return null;
  }
}

interface UserDraftGroup {
  route: string;
  entries: DraftEntry[];
}

function findAllUserDrafts(userId: string): UserDraftGroup[] {
  const prefix = `seweb:draft:`;
  const suffix = `:${userId}`;
  const results: UserDraftGroup[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(prefix) || !key.endsWith(suffix)) continue;
    try {
      const entries: DraftEntry[] = JSON.parse(localStorage.getItem(key) ?? "[]");
      const validEntries = entries.filter((e) => !isExpired(e.expiresAt));
      if (validEntries.length > 0) {
        const route = key.slice(prefix.length, key.length - suffix.length);
        results.push({ route, entries: validEntries });
      }
    } catch {
      // corrupt entry — skip
    }
  }
  return results;
}

function getPreservedState(userId: string): PreservedState | null {
  try {
    const raw = localStorage.getItem(`seweb:state:${userId}`);
    if (!raw) return null;
    const state: PreservedState = JSON.parse(raw);
    if (isExpired(state.expiresAt)) {
      localStorage.removeItem(`seweb:state:${userId}`);
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

function cleanupExpiredEntries(userId: string): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    if (key.startsWith("seweb:draft:") && key.endsWith(`:${userId}`)) {
      try {
        const entries: DraftEntry[] = JSON.parse(localStorage.getItem(key) ?? "[]");
        const allExpired = entries.every((e) => isExpired(e.expiresAt));
        if (allExpired) keysToRemove.push(key);
      } catch {
        keysToRemove.push(key);
      }
    }

    if (key === `seweb:state:${userId}`) {
      try {
        const state: PreservedState = JSON.parse(localStorage.getItem(key) ?? "{}");
        if (state.expiresAt && isExpired(state.expiresAt)) keysToRemove.push(key);
      } catch {
        keysToRemove.push(key);
      }
    }
  }

  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
}

export function usePostMaintenanceRestore(): PostMaintenanceRestoreResult {
  const navigate = useNavigate();
  const userId = getUserId();

  const [showReturnBanner, setShowReturnBanner] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [draftRoute, setDraftRoute] = useState<string | null>(null);
  const [draftTimestamp, setDraftTimestamp] = useState<string | null>(null);

  // Mount: cleanup expired entries and detect recently ended maintenance
  useEffect(() => {
    if (!userId) return;

    cleanupExpiredEntries(userId);

    const flag = getRecentlyEndedFlag();
    if (!flag) return;

    setShowReturnBanner(true);

    const allDrafts = findAllUserDrafts(userId);
    const stateInfo = getPreservedState(userId);

    if (allDrafts.length > 0) {
      const firstDraft = allDrafts[0];
      setHasDraft(true);
      setDraftCount(allDrafts.length);
      setDraftRoute(stateInfo?.route ?? `/${firstDraft.route}`);
      setDraftTimestamp(firstDraft.entries[0]?.savedAt ?? null);
    } else if (stateInfo) {
      setHasDraft(false);
      setDraftCount(0);
      setDraftRoute(stateInfo.route);
    }

    // Story 4.3 AC3/AC4: Auto-dismiss banner after 30s
    const timer = setTimeout(() => setShowReturnBanner(false), 30_000);
    return () => clearTimeout(timer);
  }, [userId]);

  const restoreDraft = useCallback(() => {
    if (!userId) return;

    const stateInfo = getPreservedState(userId);
    const allDrafts = findAllUserDrafts(userId);
    const firstDraft = allDrafts[0] ?? null;
    const targetRoute = stateInfo?.route ?? (firstDraft ? `/${firstDraft.route}` : null);

    if (targetRoute) {
      navigate(targetRoute);
    }

    if (firstDraft) {
      window.dispatchEvent(
        new CustomEvent("maintenance:restore-draft", {
          detail: {
            route: firstDraft.route,
            entries: firstDraft.entries,
          },
        })
      );
    }

    // Restore scroll position
    if (stateInfo?.scrollPosition) {
      requestAnimationFrame(() => {
        window.scrollTo(stateInfo.scrollPosition.x, stateInfo.scrollPosition.y);
      });
    }

    setShowReturnBanner(false);
    setHasDraft(false);
    setDraftCount(0);
  }, [navigate, userId]);

  const dismissBanner = useCallback(() => {
    setShowReturnBanner(false);
  }, []);

  const reopenBanner = useCallback(() => {
    setShowReturnBanner(true);
  }, []);

  const dismissDraft = useCallback(() => {
    if (!userId) return;

    // Remove all draft keys for this user
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("seweb:draft:") && key.endsWith(`:${userId}`)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }

    // Remove state
    localStorage.removeItem(`seweb:state:${userId}`);

    setHasDraft(false);
    setDraftRoute(null);
    setDraftTimestamp(null);
  }, [userId]);

  return {
    showReturnBanner,
    hasDraft,
    draftCount,
    draftRoute,
    draftTimestamp,
    restoreDraft,
    dismissBanner,
    dismissDraft,
    reopenBanner,
  };
}
